<template>
  <div v-if="!loading">
    <b-form-group
      :label="$t('field.reference')"
      v-if="isUpdateForm"
      class="col-6 pl-0"
    >
      <b-form-input
        v-model="item.reference"
        name="reference"
        type="text"
        disabled
      />
    </b-form-group>
    <b-row>
      <b-col>
        <b-form-group :label="$t('field.company')">
          <multi-select
            v-model="company"
            :multiple="false"
            :options="companies"
            :allow-empty="false"
            :searchable="true"
            :placeholder="$t('actions.pick_company')"
            :show-labels="false"
            label="label"
            track-by="id"
            @input="updateCompany"
            disabled
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="$t('field.type')">
          <multi-select
            v-model="type"
            :multiple="false"
            :options="types"
            :allow-empty="false"
            :searchable="true"
            :placeholder="$t('actions.pick_type')"
            :show-labels="false"
            label="label"
            track-by="id"
            @input="updateType"
            :disabled="disableBudget"
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group :label="$t('field.client')">
          <multi-select
            v-model="client"
            :multiple="false"
            :options="clients"
            :allow-empty="false"
            :searchable="true"
            :placeholder="$t('actions.pick_client')"
            :show-labels="false"
            label="name"
            track-by="id"
            @input="updateClient"
            :disabled="disableBudget"
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="$t('field.responsible')">
          <multi-select
            v-model="user"
            :multiple="false"
            :options="users"
            :allow-empty="false"
            :searchable="true"
            :placeholder="$t('actions.pick_user')"
            :show-labels="false"
            label="name"
            track-by="id"
            @input="updateUser"
            :disabled="disableBudget"
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="col-6">
        <b-form-group :label="$t('field.workflow')">
          <multi-select
            v-model="workflow"
            :multiple="false"
            :options="workflows"
            :allow-empty="false"
            :searchable="true"
            :placeholder="$t('actions.pick_workflow')"
            :show-labels="false"
            label="label"
            track-by="id"
            @input="updateWorkflow"
            :disabled="disableBudget"
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
    </b-row>
  </div>
  <div v-else>
    <spinner />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import companiesApi from '@/api/companies'
import typesApi from '@/api/types'
import workflowsApi from '@/api/workflows'
import clientsApi from '@/api/clients'
import usersApi from '@/api/users'
import MultiSelect from 'vue-multiselect'

export default {
  components: {
    MultiSelect
  },
  watch: {
    '$store.state.form.relationshipFields.status': {
      handler(newVal) {
        this.proposalStatus = newVal
        this.checkProposalStatus()
      }
    },
    '$store.state.company.activeCompany': {
      async handler() {
        this.loading = true

        await this.init()

        this.loading = false
      }
    }
  },
  data: () => ({
    loading: true,
    companiesApi,
    typesApi,
    workflowsApi,
    clientsApi,
    usersApi,
    companies: [],
    company: null,
    types: [],
    type: null,
    workflows: [],
    workflow: null,
    clients: [],
    client: null,
    users: [],
    user: null,
    disableBudget: false,
    proposalStatus: null
  }),
  computed: {
    ...mapGetters({
      item: 'form/fields',
      proposalItem: 'form/relationshipFields',
      activeCompany: 'company/activeCompany'
    }),
    isResponsibleUser() {
      return this.item.user_id === this.$auth.user().id
    },
    isUpdateForm() {
      return this.$route.name.includes('Update')
    },
    filterData() {
      return { company_id: this.activeCompany.id }
    }
  },
  created() {
    this.init()
  },
  methods: {
    async init() {
      await Promise.all([
        this.getCompanies(),
        this.getTypes(),
        this.getWorkflows(),
        this.getClients(),
        this.getUsers()
      ])

      this.setCompany()
      this.setType()
      this.setWorkflow()
      this.setClient()
      this.setUser()
      this.checkProposalStatus()

      this.loading = false
    },
    async getCompanies() {
      try {
        const response = await this.companiesApi.all()

        if (response && response.data && response.data.list) {
          this.companies = response.data.list.map(item => ({
            id: item.id,
            label: item.name
          }))
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    setCompany() {
      this.company = this.item.company_id
        ? this.companies.find(item => item.id === this.item.company_id)
        : this.companies.find(item => item.id === this.activeCompany.id)

      this.item.company_id = this.company.id

      if (this.company === undefined) {
        this.$toasted.error(
          this.$t('messages.not_found_foreign', {
            field: this.$t('field.company').toLowerCase()
          })
        )
      }
    },
    updateCompany(company) {
      this.item.company_id = company.id
      this.item.company = company
    },
    async getTypes() {
      try {
        const response = await this.typesApi.all()

        if (response && response.data && response.data.list) {
          this.types = response.data.list.map(item => ({
            id: item.id,
            label: item.name
          }))
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    setType() {
      this.type = this.item.type_id
        ? this.types.find(item => item.id === this.item.type_id)
        : null

      if (this.type === undefined) {
        this.$toasted.error(
          this.$t('messages.not_found_foreign', {
            field: this.$t('field.type').toLowerCase()
          })
        )
      }
    },
    updateType(type) {
      this.item.type_id = type.id
    },
    async getWorkflows() {
      try {
        const response = await this.workflowsApi.all(this.filterData)

        if (response && response.data && response.data.list) {
          this.workflows = response.data.list.map(item => ({
            id: item.id,
            label: item.name
          }))
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    setWorkflow() {
      this.workflow = this.item.workflow_id
        ? this.workflows.find(item => item.id === this.item.workflow_id)
        : null

      if (this.workflow === undefined) {
        this.$toasted.error(
          this.$t('messages.not_found_foreign', {
            field: this.$t('field.workflow').toLowerCase()
          })
        )
      }
    },
    updateWorkflow(workflow) {
      this.item.workflow_id = workflow.id
    },

    async getClients() {
      try {
        const response = await this.clientsApi.all(this.filterData)

        if (response && response.data && response.data.list) {
          this.clients = response.data.list
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    setClient() {
      this.client = this.item.client_id
        ? this.clients.find(item => item.id === this.item.client_id)
        : null

      if (this.client === undefined) {
        this.$toasted.error(
          this.$t('messages.not_found_foreign', {
            field: this.$t('field.client').toLowerCase()
          })
        )
      }
    },
    updateClient(client) {
      this.item.client = client
      this.item.client_id = client.id
    },
    async getUsers() {
      try {
        const response = await this.usersApi.all(this.filterData)

        if (response && response.data && response.data.list) {
          this.users = response.data.list
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    setUser() {
      this.user = this.item.user_id
        ? this.users.find(item => item.id === this.item.user_id)
        : this.users.find(item => item.id === this.$auth.user().id)

      if (this.user) {
        this.item.user_id = this.user.id
      }

      if (this.user === undefined) {
        this.$toasted.error(
          this.$t('messages.not_found_foreign', {
            field: this.$t('field.responsible').toLowerCase()
          })
        )
      }
    },
    updateUser(user) {
      this.item.user = user
      this.item.user_id = user.id
    },
    checkProposalStatus() {
      if (
        (!this.proposalStatus || this.proposalStatus.id === 1) &&
        this.isResponsibleUser
      ) {
        this.disableBudget = false
      } else {
        this.disableBudget = true
      }
    }
  }
}
</script>
